(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const observed = new WeakSet();
  const excludedSectionNames = [
    'header',
    'announcement',
    'cart',
    'predictive-search',
    'popup',
    'toolbar',
    'sticky-notification',
    'before-you-leave',
    'age-verification'
  ];
  const heroSectionNames = ['slideshow', 'media-banner', 'collection-banner'];
  const productSectionNames = [
    'featured-collection',
    'product-recommendations',
    'related-products',
    'recently-viewed-products',
    'collection-product-grid'
  ];
  const collectionSectionNames = ['collection-list', 'editorial-collections'];
  const visualSectionNames = [
    'blog-posts',
    'featured-article',
    'featured-product',
    'faqs',
    'lookbook',
    'multitab-image',
    'video-carousel'
  ];
  let observer;
  let parallaxObserver;
  let parallaxFrameRequested = false;
  const parallaxElements = new Set();

  const includesAny = (value, terms) => terms.some((term) => value.includes(term));

  const reveal = (element) => {
    element.classList.remove('premium-scroll--pending');
    element.classList.add('premium-scroll--visible');
    element.removeAttribute('aria-hidden');
    observer?.unobserve(element);
  };

  const getObserver = () => {
    if (observer || !('IntersectionObserver' in window)) return observer;

    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) reveal(entry.target);
      });
    }, {
      rootMargin: '0px 0px -8% 0px',
      threshold: 0.08
    });

    return observer;
  };

  const register = (element, type = 'fade-up', delay = 0) => {
    if (
      !element ||
      observed.has(element) ||
      element.classList.contains('scroll-trigger') ||
      element.closest('.scroll-trigger')
    ) return;

    observed.add(element);
    element.classList.add('premium-scroll', `premium-scroll--${type}`);
    if (type === 'card-lift') element.classList.add('premium-card-lift');
    if (type === 'image-parallax-soft') registerParallax(element);
    element.style.setProperty('--premium-reveal-delay', `${Math.min(delay, 420)}ms`);
    element.dataset.scrollAnimationState = 'registered';

    if (type === 'text-line-reveal') {
      Array.from(element.children).forEach((child, index) => {
        child.style.setProperty('--premium-line-index', index);
      });
    }

    if (reduceMotion.matches || window.Shopify?.designMode) {
      reveal(element);
      return;
    }

    element.classList.add('premium-scroll--pending');
    const position = element.getBoundingClientRect();
    if (position.top < window.innerHeight * 0.92 && position.bottom > 0) {
      requestAnimationFrame(() => reveal(element));
      return;
    }

    const intersectionObserver = getObserver();
    if (intersectionObserver) {
      intersectionObserver.observe(element);
    } else {
      reveal(element);
    }
  };

  const registerDeclarativeHooks = (root) => {
    root.querySelectorAll('[data-scroll-animation]').forEach((element) => {
      register(element, element.dataset.scrollAnimation || 'fade-up');
    });

    root.querySelectorAll('[data-scroll-stagger]').forEach((group) => {
      const type = group.dataset.scrollStagger || 'fade-up';
      const items = group.querySelectorAll(':scope > *, [data-scroll-stagger-item]');
      const interval = type === 'stagger-soft' ? 90 : 70;
      items.forEach((item, index) => register(item, type, index * interval));
    });

    root.querySelectorAll('[data-scroll-parallax="soft"]').forEach(registerParallax);
  };

  const registerCards = (section, type, selectors) => {
    const cards = section.querySelectorAll(selectors);
    cards.forEach((card, index) => register(card, type, (index % 6) * 70));
    return cards.length;
  };

  function registerParallax(element) {
    if (!element || parallaxElements.has(element) || reduceMotion.matches) return;

    parallaxElements.add(element);
    element.classList.add('premium-parallax-media');
    parallaxObserver ||= new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        entry.target.dataset.premiumParallaxActive = entry.isIntersecting ? 'true' : 'false';
      });
    }, { rootMargin: '15% 0px' });
    parallaxObserver.observe(element);
  }

  const updateParallax = () => {
    parallaxFrameRequested = false;
    parallaxElements.forEach((element) => {
      if (element.dataset.premiumParallaxActive !== 'true') return;
      const rect = element.getBoundingClientRect();
      const progress = (rect.top + rect.height * 0.5 - window.innerHeight * 0.5) / window.innerHeight;
      const offset = Math.max(-18, Math.min(18, progress * -18));
      element.style.setProperty('--premium-parallax-y', `${offset}px`);
    });
  };

  const requestParallaxUpdate = () => {
    if (parallaxFrameRequested || !parallaxElements.size) return;
    parallaxFrameRequested = true;
    requestAnimationFrame(updateParallax);
  };

  const enhanceSection = (section) => {
    const sectionName = `${section.id} ${section.className}`.toLowerCase();
    if (includesAny(sectionName, excludedSectionNames)) return;

    registerDeclarativeHooks(section);

    if (includesAny(sectionName, heroSectionNames)) {
      const hero = section.querySelector('.slideshow, .media-banner, .collection-hero, .banner') || section.firstElementChild;
      register(hero, 'fade-in');
      const heroContent = section.querySelector('.swiper-slide-active .slide__content, .banner__content');
      const heroMedia = section.querySelector('.swiper-slide-active .slide__img-wrapper, .banner__media');
      register(heroContent, 'text-line-reveal');
      if (!section.querySelector('.scroll-fade-slideshow')) register(heroMedia, 'image-zoom-in');
      return;
    }

    if (sectionName.includes('media-with-text')) {
      const media = section.querySelector('.media-withtext__content, .media-banner');
      const content = section.querySelector('.section-resource-list__content, .media-banner__content');
      const mediaOnRight = Boolean(section.querySelector('.media-banner--media-right'));
      register(media, mediaOnRight ? 'reveal-right' : 'reveal-left');
      register(content, mediaOnRight ? 'reveal-left' : 'reveal-right', 80);
      return;
    }

    if (includesAny(sectionName, productSectionNames)) {
      const cardCount = registerCards(
        section,
        'stagger-soft',
        '.product-card-wrapper:not(.swiper-slide), .grid__item:not(.swiper-slide) > .card-wrapper'
      );
      section.querySelectorAll('.product-card-wrapper, .card-wrapper').forEach((card) => card.classList.add('premium-card-lift'));
      if (!cardCount) register(section.querySelector('.section'), 'fade-up');
      return;
    }

    if (includesAny(sectionName, collectionSectionNames)) {
      const cardCount = registerCards(
        section,
        'scale-in',
        '.collection-card-wrapper, .collection-list .grid__item:not(.swiper-slide), [class*="collection-card"]:not(.swiper-slide)'
      );
      section.querySelectorAll('.collection-card-wrapper, [class*="collection-card"]').forEach((card) => card.classList.add('premium-card-lift'));
      if (!cardCount) register(section.querySelector('.section'), 'zoom-soft');
      return;
    }

    const icons = section.querySelectorAll('.icon-block');
    if (icons.length > 1) {
      icons.forEach((icon, index) => register(icon, 'stagger-soft', (index % 6) * 90));
      return;
    }

    if (sectionName.includes('testimonial') || sectionName.includes('review')) {
      registerCards(section, 'blur-in', '[class*="testimonial"], [class*="review"]');
      return;
    }

    if (sectionName.includes('footer')) {
      register(section.querySelector('.footer, .footer-bottom') || section.firstElementChild, 'fade-in');
      return;
    }

    if (includesAny(sectionName, visualSectionNames)) {
      register(section.querySelector('.section') || section.firstElementChild, 'fade-up');
    }
  };

  const initialize = (root = document) => {
    registerDeclarativeHooks(root);
    if (root.matches?.('[id^="shopify-section-"]')) enhanceSection(root);
    root.querySelectorAll('[id^="shopify-section-"]').forEach(enhanceSection);
  };

  const initializeScrollFadeSlideshow = (section) => {
    if (
      !section ||
      section.dataset.scrollFadeSlideshow !== 'true' ||
      section.dataset.scrollFadeReady === 'true' ||
      window.Shopify?.designMode ||
      reduceMotion.matches
    ) return;

    const swiperComponent = section.querySelector('swiper-component');
    const slides = Array.from(swiperComponent?.querySelectorAll('.swiper-wrapper > .swiper-slide') || [])
      .filter((slide) => !slide.classList.contains('swiper-slide-duplicate'));
    if (!swiperComponent || slides.length < 2) return;

    section.dataset.scrollFadeReady = 'true';
    let activeIndex = 0;
    let transitionLocked = false;
    let hasCompleted = false;
    let wheelAccumulator = 0;
    let wheelResetTimer;
    let touchStartX = 0;
    let touchStartY = 0;
    let touchTracking = false;
    let touchConsumed = false;
    let touchAxis;
    const mobileViewport = window.matchMedia('(max-width: 989px)');

    const updateThemePagination = () => {
      const bullets = Array.from(section.querySelectorAll('.swiper-pagination-bullet'));
      const stateClasses = [
        'swiper-pagination-bullet-active',
        'swiper-pagination-bullet-active-main',
        'swiper-pagination-bullet-active-prev',
        'swiper-pagination-bullet-active-prev-prev',
        'swiper-pagination-bullet-active-next',
        'swiper-pagination-bullet-active-next-next'
      ];

      bullets.forEach((bullet, index) => {
        bullet.classList.remove(...stateClasses);
        bullet.removeAttribute('aria-current');
        if (index === activeIndex) {
          bullet.classList.add('swiper-pagination-bullet-active', 'swiper-pagination-bullet-active-main');
          bullet.setAttribute('aria-current', 'true');
        } else if (index === activeIndex - 1) {
          bullet.classList.add('swiper-pagination-bullet-active-prev');
        } else if (index === activeIndex - 2) {
          bullet.classList.add('swiper-pagination-bullet-active-prev-prev');
        } else if (index === activeIndex + 1) {
          bullet.classList.add('swiper-pagination-bullet-active-next');
        } else if (index === activeIndex + 2) {
          bullet.classList.add('swiper-pagination-bullet-active-next-next');
        }
      });
    };

    const showSlide = (nextIndex) => {
      activeIndex = nextIndex;
      hasCompleted = activeIndex === slides.length - 1;
      transitionLocked = true;
      requestAnimationFrame(() => {
        slides.forEach((slide, index) => {
          const isActive = index === activeIndex;
          slide.classList.toggle('is-scroll-fade-active', isActive);
          slide.style.zIndex = isActive ? '2' : '1';
          slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });
        updateThemePagination();
      });
      window.setTimeout(() => {
        transitionLocked = false;
      }, 560);
    };

    const handleWheel = (event) => {
      if (event.ctrlKey || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      if (event.deltaY <= 0) {
        wheelAccumulator = 0;
        return;
      }

      const rect = section.getBoundingClientRect();
      const visibleHeight = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
      const activationHeight = Math.min(rect.height * 0.35, 240);
      if (visibleHeight < activationHeight) return;

      if (transitionLocked) {
        event.preventDefault();
        return;
      }
      if (hasCompleted) return;

      if (activeIndex >= slides.length - 1) return;

      event.preventDefault();

      wheelAccumulator += event.deltaY;
      window.clearTimeout(wheelResetTimer);
      wheelResetTimer = window.setTimeout(() => {
        wheelAccumulator = 0;
      }, 180);
      if (Math.abs(wheelAccumulator) < 24) return;

      wheelAccumulator = 0;
      showSlide(Math.min(slides.length - 1, activeIndex + 1));
    };

    const handlePaginationClick = (event) => {
      const bullet = event.target.closest('.swiper-pagination-bullet');
      if (!bullet) return;

      const bullets = Array.from(section.querySelectorAll('.swiper-pagination-bullet'));
      const clickedIndex = bullets.indexOf(bullet);
      if (clickedIndex < 0 || clickedIndex >= slides.length) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      showSlide(clickedIndex);
    };

    const handleTouchStart = (event) => {
      if (!mobileViewport.matches || event.touches.length !== 1) return;
      touchStartX = event.touches[0].clientX;
      touchStartY = event.touches[0].clientY;
      touchTracking = true;
      touchConsumed = false;
      touchAxis = undefined;
    };

    const handleTouchMove = (event) => {
      if (!mobileViewport.matches || !touchTracking || event.touches.length !== 1) return;

      const horizontalDistance = touchStartX - event.touches[0].clientX;
      const verticalDistance = touchStartY - event.touches[0].clientY;
      if (touchConsumed) {
        event.preventDefault();
        return;
      }

      if (!touchAxis) {
        if (Math.max(Math.abs(horizontalDistance), Math.abs(verticalDistance)) < 10) return;
        touchAxis = Math.abs(horizontalDistance) > Math.abs(verticalDistance) ? 'horizontal' : 'vertical';
      }

      if (touchAxis === 'horizontal') {
        event.preventDefault();
        if (transitionLocked) {
          touchConsumed = true;
          return;
        }
        if (Math.abs(horizontalDistance) < 48) return;

        touchConsumed = true;
        const direction = horizontalDistance > 0 ? 1 : -1;
        const nextIndex = Math.max(0, Math.min(slides.length - 1, activeIndex + direction));
        if (nextIndex !== activeIndex) showSlide(nextIndex);
        return;
      }

      if (verticalDistance <= 0 || hasCompleted || activeIndex >= slides.length - 1) return;

      const rect = section.getBoundingClientRect();
      const visibleHeight = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
      const activationHeight = Math.min(rect.height * 0.35, 180);
      if (visibleHeight < activationHeight) return;

      event.preventDefault();
      if (transitionLocked) {
        touchConsumed = true;
        return;
      }
      if (verticalDistance < 36) return;

      touchConsumed = true;
      showSlide(Math.min(slides.length - 1, activeIndex + 1));
    };

    const handleTouchEnd = () => {
      touchTracking = false;
      touchConsumed = false;
      touchAxis = undefined;
    };

    let swiperAttempts = 0;
    const waitForSwiper = () => {
      swiperAttempts += 1;
      const swiper = swiperComponent.initSwiper;
      if (swiper && !swiper.destroyed) {
        swiper.autoplay?.stop();
        if (mobileViewport.matches) {
          swiper.allowTouchMove = false;
          swiper.params.allowTouchMove = false;
        }
        const pagination = section.querySelector('.swiper-pagination');
        pagination?.addEventListener('click', handlePaginationClick, true);
        updateThemePagination();
        return;
      }
      if (swiperAttempts > 240) return;
      requestAnimationFrame(waitForSwiper);
    };

    slides.forEach((slide, index) => {
      slide.classList.toggle('is-scroll-fade-active', index === 0);
      slide.style.zIndex = index === 0 ? '2' : '1';
      slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
    });

    window.addEventListener('wheel', handleWheel, { passive: false });
    section.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });
    window.addEventListener('touchcancel', handleTouchEnd, { passive: true });
    requestAnimationFrame(waitForSwiper);
  };

  initialize();

  document.querySelectorAll('[data-scroll-fade-slideshow="true"]').forEach(initializeScrollFadeSlideshow);
  window.addEventListener('scroll', requestParallaxUpdate, { passive: true });

  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
    initializeScrollFadeSlideshow(event.target.querySelector('[data-scroll-fade-slideshow="true"]'));
  });
  reduceMotion.addEventListener('change', () => {
    if (reduceMotion.matches) {
      document.querySelectorAll('.premium-scroll--pending').forEach(reveal);
    }
  });
})();
